import { useCallback } from 'react'
import { Table } from '@tanstack/react-table'
import { Button } from '@/components/shadcn/button'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconDocumentExportOutline } from '@icons/IconDocumentExportOutline'
import { DataTableV2DataRow } from '../types'
import { exportToExcelV2, flattenGroupedData } from '../utils/exportUtilsV2'
import { cn } from '@/lib/utils'

interface ExportButtonV2Props {
  table?: Table<DataTableV2DataRow>
  data: DataTableV2DataRow[]
  title?: string
  selectionRequired: boolean
  className?: string
}

export function ExportButtonV2({ table, data, title, selectionRequired, className }: ExportButtonV2Props) {
  const selectedRowCount = table ? Object.keys(table.getState().rowSelection).length : 0
  const isDisabled = selectionRequired && selectedRowCount === 0

  const handleExport = useCallback(async () => {
    const date = new Date().toLocaleDateString()
    const fileName = title ? `${title} - ${date}` : `export - ${date}`

    let exportData: DataTableV2DataRow[]

    if (selectionRequired) {
      if (!table) {
        return
      }

      // Export only selected rows
      const selectedRows = table.getSelectedRowModel().rows
      const selectedOriginals = selectedRows.map((row) => row.original)
      exportData = flattenGroupedData(selectedOriginals)

      if (exportData.length === 0) {
        return
      }
    } else {
      // Export all data - flatten if grouped
      exportData = flattenGroupedData(data)
    }

    await exportToExcelV2(exportData, fileName)
  }, [table, data, title, selectionRequired])

  // Don't render if selection is required but nothing is selected
  if (isDisabled) {
    return (
      <TooltipButton tooltip="Select rows to export">
        <div>
          <Button size="icon" variant="ghost" className="size-8 rounded-full opacity-50" disabled>
            <IconDocumentExportOutline className="size-4" />
          </Button>
        </div>
      </TooltipButton>
    )
  }

  return (
    <TooltipButton tooltip={selectionRequired ? `Export ${selectedRowCount} selected rows` : 'Export to Excel'}>
      <Button size="icon" variant="ghost" className={cn('size-8 rounded-full', className)} onClick={handleExport}>
        <IconDocumentExportOutline className="size-4" />
      </Button>
    </TooltipButton>
  )
}
